import React from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TrendingUp, TrendingDown, Target } from "lucide-react";
import { formatLKR } from "@/lib/api";

/**
 * Test 2 Fix 1 — Two-line target cell with click popover.
 *
 * Display rules:
 *  - Line 1: Rs.[base case target] (PE-historical method, fall back to
 *    target_average when PE-based is missing).
 *  - Line 2: [+/-pct]% with arrow. Green when stock is BELOW target
 *    (upside), red when stock is ABOVE target (downside).
 *  - On click → show a popover with all four methods (PE, PB, DDM,
 *    Graham), the average, and implied upside.
 */

const NoTarget = () => <span className="text-muted-foreground">—</span>;

const MethodRow = ({ label, value, currentPrice }) => {
  const has = value != null && value > 0;
  const pct =
    has && currentPrice && currentPrice > 0
      ? ((value - currentPrice) / currentPrice) * 100
      : null;
  const tone =
    pct == null
      ? "text-muted-foreground"
      : pct > 0
        ? "text-[hsl(var(--success))]"
        : pct < 0
          ? "text-[hsl(var(--destructive))]"
          : "text-muted-foreground";
  return (
    <div className="flex items-center justify-between gap-3 py-1.5 border-b last:border-0 border-border/60">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <div className="flex items-baseline gap-2 tabular-nums">
        <span className="font-display text-sm font-semibold">
          {has ? formatLKR(value) : "—"}
        </span>
        <span className={`text-[10px] font-medium ${tone}`}>
          {pct == null ? "" : `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`}
        </span>
      </div>
    </div>
  );
};

export default function TargetCellPopover({
  cseSymbol,
  companyName,
  currentPrice,
  targetPe,
  targetPb,
  targetDdm,
  targetGraham,
  targetAverage,
  targetUpsidePct,
  targetBaseCase,
  targetBaseCaseUpsidePct,
  testId,
}) {
  // Test 6 Fix 1 — Use the canonical weighted base case (PE 40% + PB 40%
  // + DDM 20%). This MUST match the headline shown on the Stock Detail
  // page so users never see a different price across screens. Falls back
  // to the simple 4-method average when base case is missing on a doc
  // that hasn't been recomputed yet.
  const basePrice =
    targetBaseCase != null && targetBaseCase > 0
      ? targetBaseCase
      : targetAverage != null && targetAverage > 0
        ? targetAverage
        : null;

  const baseUpside =
    targetBaseCaseUpsidePct != null
      ? targetBaseCaseUpsidePct
      : basePrice != null && currentPrice && currentPrice > 0
        ? ((basePrice - currentPrice) / currentPrice) * 100
        : null;

  if (basePrice == null || baseUpside == null) {
    return (
      <span data-testid={testId}>
        <NoTarget />
      </span>
    );
  }

  // Color rule: stock BELOW target → green (upside, +%, ↑)
  //             stock ABOVE target → red (downside, -%, ↓)
  const isUpside = baseUpside > 0;
  const tone = isUpside
    ? "text-[hsl(var(--success))]"
    : baseUpside < 0
      ? "text-[hsl(var(--destructive))]"
      : "text-muted-foreground";
  const Arrow = isUpside ? TrendingUp : TrendingDown;
  const sign = baseUpside > 0 ? "+" : "";

  // Methods used count (for popover header)
  const methodsUsed = [targetPe, targetPb, targetDdm, targetGraham].filter(
    (v) => v != null && v > 0,
  ).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex flex-col items-end gap-0 -m-1 p-1 rounded hover:bg-muted/60 focus:bg-muted/60 focus:outline-none focus:ring-1 focus:ring-primary/30 transition-colors duration-150"
          aria-label={`Target price details for ${companyName || cseSymbol}`}
          data-testid={testId}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="font-display font-semibold tabular-nums leading-tight">
            {formatLKR(basePrice)}
          </span>
          <span
            className={`inline-flex items-center gap-0.5 text-[11px] tabular-nums font-medium leading-tight ${tone}`}
          >
            {sign}
            {baseUpside.toFixed(1)}%
            <Arrow className="h-3 w-3" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                <Target className="h-3 w-3" />
                Target price analysis
              </div>
              <div className="font-display text-base font-semibold mt-0.5">
                {companyName || cseSymbol}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-muted-foreground">
                Current
              </div>
              <div className="font-display text-sm tabular-nums">
                {formatLKR(currentPrice)}
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border/60 px-3 py-2">
            <MethodRow
              label="PE × historical (40%)"
              value={targetPe}
              currentPrice={currentPrice}
            />
            <MethodRow
              label="PB × 1.5 (40%)"
              value={targetPb}
              currentPrice={currentPrice}
            />
            <MethodRow
              label="Dividend discount (20%)"
              value={targetDdm}
              currentPrice={currentPrice}
            />
            <MethodRow
              label="Graham number (ref)"
              value={targetGraham}
              currentPrice={currentPrice}
            />
          </div>

          <div className="flex items-center justify-between gap-3 pt-1">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">
                Base case (weighted)
              </div>
              <div className="font-display text-base font-semibold tabular-nums">
                {basePrice != null ? formatLKR(basePrice) : "—"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase text-muted-foreground">
                Upside vs price
              </div>
              <div
                className={`font-display text-base font-semibold tabular-nums ${
                  baseUpside == null
                    ? "text-muted-foreground"
                    : baseUpside > 0
                      ? "text-[hsl(var(--success))]"
                      : baseUpside < 0
                        ? "text-[hsl(var(--destructive))]"
                        : "text-muted-foreground"
                }`}
              >
                {baseUpside == null
                  ? "—"
                  : `${baseUpside > 0 ? "+" : ""}${baseUpside.toFixed(1)}%`}
              </div>
            </div>
          </div>

          <p className="text-[10px] text-muted-foreground leading-snug">
            12-month target (base case = PE 40% + PB 40% + DDM 20%
            weighted). Average of all 4 methods:{" "}
            {targetAverage != null ? formatLKR(targetAverage) : "—"}.
            Click the stock row to open the full Stock Detail page for
            deeper context.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
